import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "./api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setAccount(data.account);
      setProfiles(data.profiles || []);
      setSessionExpiresAt(data.session_expires_at || null);
      return data;
    } catch (e) {
      setAccount(null);
      setProfiles([]);
      setSessionExpiresAt(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, skip /me — AuthCallback will handle it
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    setAccount(null);
    setProfiles([]);
    setSessionExpiresAt(null);
    window.location.href = "/";
  }, []);

  useEffect(() => {
    if (!account || !sessionExpiresAt) return undefined;
    const expiresAtMs = Date.parse(sessionExpiresAt);
    if (Number.isNaN(expiresAtMs)) return undefined;

    const timeoutId = window.setTimeout(
      () => { void logout(); },
      Math.max(0, expiresAtMs - Date.now()),
    );
    return () => window.clearTimeout(timeoutId);
  }, [account, sessionExpiresAt, logout]);

  const activeProfile = profiles.find((p) => p.profile_id === account?.active_profile_id) || profiles[0];

  const switchProfile = async (profile_id) => {
    await api.patch("/accounts/active-profile", { profile_id });
    await refresh();
  };

  return (
    <AuthContext.Provider value={{ account, profiles, activeProfile, loading, refresh, logout, switchProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
